import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api.js';
import { t, getal } from '../i18n.js';
import { ICOON } from './iconen.jsx';

/**
 * De IP-reeksen van een klant (docs/besluiten/nessus-koppeling.md): een adres dat Nessus scant en in een reeks
 * valt, hoort bij deze klant. Een nieuwe reeks neemt ook mee wat al gescand was en nog bij niemand hoorde.
 */
export default function IpReeksen({ klant, onSluit }) {
  const client = useQueryClient();
  const q = useQuery({ queryKey: ['ip-reeksen', klant.code], queryFn: () => api.ipReeksen(klant.code) });
  const [nieuw, zetNieuw] = useState({ cidr: '', description: '' });
  const [melding, zetMelding] = useState(null);
  const [bezig, zetBezig] = useState(false);
  const doe = async (fn) => {
    if (bezig) return;
    zetMelding(null); zetBezig(true);
    try { const m = await fn(); if (m) zetMelding({ goed: true, tekst: m }); await q.refetch(); client.invalidateQueries({ queryKey: ['scans'] }); }
    catch (e) { zetMelding({ goed: false, tekst: t(e.details ? Object.values(e.details)[0] : e.message) }); }
    finally { zetBezig(false); }
  };

  return (
    <section className={onSluit ? 'card' : undefined} style={onSluit ? { marginTop: 14 } : undefined}>
      {onSluit && <header><h2>{t('IP-reeksen van {klant}', { klant: klant.name })}</h2>
        <span className="meta"><button type="button" className="namelink" onClick={onSluit}>{t('Sluiten')}</button></span></header>}
      {melding && <div className={`melder ${melding.goed ? 'blauw' : 'rood'}`} style={{ marginBottom: 10 }}>{melding.tekst}</div>}
      {q.isPending ? <p className="muted">{t('Laden…')}</p> : q.isError ? <div className="melder rood">{t(q.error.message)}</div> : q.data.data.length ? (
        <table>
          <thead><tr><th>{t('Reeks')}</th><th>{t('Omschrijving')}</th><th style={{ textAlign: 'right' }}>{t('Gescande adressen')}</th><th /></tr></thead>
          <tbody>{q.data.data.map((r) => (
            <tr key={r.id}>
              <td className="mono">{r.cidr}</td><td>{r.description ?? ''}</td><td className="num" style={{ textAlign: 'right' }}>{getal(r.addresses)}</td>
              <td style={{ textAlign: 'right' }}><button type="button" className="icoonknop" title={t('Verwijderen')} aria-label={t('Verwijderen')}
                onClick={() => doe(async () => { await api.verwijderIpReeks(klant.code, r.id); })}>{ICOON.prullenbak}</button></td>
            </tr>
          ))}</tbody>
        </table>
      ) : <p className="empty">{t('Nog geen reeksen: gescande adressen van deze klant horen nu bij niemand.')}</p>}
      <form className="form" style={{ marginTop: 10 }} onSubmit={(e) => {
        e.preventDefault();
        doe(async () => {
          const r = (await api.maakIpReeks(klant.code, { cidr: nieuw.cidr.trim(), description: nieuw.description })).data;
          zetNieuw({ cidr: '', description: '' });
          return r.toegewezen ? t('Toegevoegd; {n} al gescande adressen horen nu bij deze klant.', { n: r.toegewezen }) : t('Toegevoegd.');
        });
      }}>
        <div className="two">
          <label className="field"><span>{t('Reeks of adres')}</span>
            <input className="input mono" value={nieuw.cidr} onChange={(e) => zetNieuw((x) => ({ ...x, cidr: e.target.value }))} placeholder="203.0.113.0/24" /></label>
          <label className="field"><span>{t('Omschrijving (niet verplicht)')}</span>
            <input className="input" value={nieuw.description} onChange={(e) => zetNieuw((x) => ({ ...x, description: e.target.value }))} placeholder={t('Bijvoorbeeld: glasvezel kantoor Rotterdam')} /></label>
        </div>
        <div className="actions"><button className="btn primary" disabled={bezig || !nieuw.cidr.trim()}>{t('Reeks toevoegen')}</button></div>
        <p className="noot">{t('Een reeks mag niet overlappen met die van een andere klant. Verwijderen haalt eerder gescande adressen niet weg bij de klant.')}</p>
      </form>
    </section>
  );
}
